/**
 * Flexible address input – accepts both prefixed (`addressStreet`) and
 * unprefixed (`street`) field names so callers can pass their objects directly.
 */
type AddressInput = Record<string, unknown> | null | undefined;

type NormalizedAddress = {
  street: string | null;
  houseNumber: string | null;
  houseLetter: string | null;
  houseNumberAddition: string | null;
  postalCode: string | null;
  city: string | null;
};

function str(value: unknown): string | null {
  if (value == null) return null;
  const s = String(value).trim();
  return s.length > 0 ? s : null;
}

function normalize(fields: AddressInput): NormalizedAddress {
  if (!fields)
    return {
      street: null,
      houseNumber: null,
      houseLetter: null,
      houseNumberAddition: null,
      postalCode: null,
      city: null,
    };
  return {
    street: str(fields.street ?? fields.addressStreet),
    houseNumber: str(fields.houseNumber ?? fields.addressHouseNumber),
    houseLetter: str(fields.houseLetter ?? fields.addressHouseLetter),
    houseNumberAddition: str(fields.houseNumberAddition ?? fields.addressHouseNumberAddition),
    postalCode: str(fields.postalCode ?? fields.addressPostalCode),
    city: str(fields.city ?? fields.addressCity),
  };
}

function buildHouseNumber(a: NormalizedAddress): string {
  let n = a.houseNumber ?? '';
  if (a.houseLetter) n += a.houseLetter;
  if (a.houseNumberAddition) n += `-${a.houseNumberAddition}`;
  return n;
}

/**
 * Full display format.
 * - Single-line (default): `"Kerkstraat 20A-1, 1234AB Amsterdam"`
 * - Multi-line: `"Kerkstraat 20A-1\n1234AB Amsterdam"`
 *
 * Accepts objects with either `addressStreet`-style or `street`-style keys.
 */
export function formatAddress(fields: AddressInput, { fallback = 'Geen adres', multiLine = false } = {}): string {
  const a = normalize(fields);
  if (!a.street) return fallback;

  const streetLine = `${a.street} ${buildHouseNumber(a)}`.trim();
  const cityLine = [a.postalCode, a.city].filter(Boolean).join(' ');
  return [streetLine, cityLine].filter(Boolean).join(multiLine ? '\n' : ', ');
}

/**
 * Compact format for email subjects: `"1234AB, 20A-1, Amsterdam"`
 */
export function formatAddressCompact(fields: AddressInput): string {
  const a = normalize(fields);
  return [a.postalCode, buildHouseNumber(a) || null, a.city].filter(Boolean).join(', ');
}

/**
 * Display-friendly postal code: `"1234AB"` → `"1234 AB"`.
 */
export function formatPostalCode(raw: string): string {
  const clean = raw.replace(/\s/g, '').toUpperCase();
  if (clean.length > 4) {
    return clean.slice(0, 4) + ' ' + clean.slice(4, 6);
  }
  return clean;
}
